import { ref, watch, onScopeDispose, type Ref } from 'vue';
import { ANIMATION_CONFIG } from '@/config/constants';
import type { Letter } from '@/types/generated';

export interface UseLetterRevealQueueOptions {
  onQueueDrained?: () => void;
}

export interface UseLetterRevealQueueReturn {
  visibleLetters: Readonly<Ref<Letter[]>>;
  isRevealing: Ref<boolean>;
  queueDrained: Ref<boolean>;
}

/**
 * Buffers incoming letters and reveals them sequentially with a stagger delay.
 *
 * Sits between raw snapshot props (allLetters) and the visible UI state.
 * When multiple letters arrive rapidly (e.g., auto-fill), they are queued
 * and revealed one at a time with LETTER_QUEUE_STAGGER_MS between each.
 *
 * On reconnection (all letters present at init), all letters are shown
 * immediately with no animation queue.
 */
export function useLetterRevealQueue(
  allLetters: Ref<Letter[]>,
  options: UseLetterRevealQueueOptions = {}
): UseLetterRevealQueueReturn {
  const visibleLetters = ref<Letter[]>([]);
  const isRevealing = ref(false);
  const queueDrained = ref(false);

  let queuedCount = 0; // How many letters have been queued (committed to reveal)
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;

  const processNext = () => {
    if (disposed) return;

    const currentAll = allLetters.value;
    if (visibleLetters.value.length >= currentAll.length) {
      // Queue fully drained
      isRevealing.value = false;
      if (currentAll.length >= 10 && !queueDrained.value) {
        queueDrained.value = true;
        options.onQueueDrained?.();
      }
      return;
    }

    // Reveal next letter
    const nextIndex = visibleLetters.value.length;
    visibleLetters.value = currentAll.slice(0, nextIndex + 1);
    isRevealing.value = true;

    // Check if this was the last one
    if (visibleLetters.value.length >= currentAll.length) {
      // All queued letters revealed; check if fully done
      if (currentAll.length >= 10 && !queueDrained.value) {
        queueDrained.value = true;
        options.onQueueDrained?.();
      }
      // Keep isRevealing true briefly for the last letter's animation
      timerId = setTimeout(() => {
        if (!disposed) {
          isRevealing.value = false;
        }
      }, ANIMATION_CONFIG.LETTER_REVEAL_DURATION);
      return;
    }

    // Schedule next reveal
    timerId = setTimeout(processNext, ANIMATION_CONFIG.LETTER_QUEUE_STAGGER_MS);
  };

  watch(
    allLetters,
    letters => {
      const newCount = letters.length;

      if (newCount <= queuedCount) return; // No new letters

      // Reconnection: all letters present at once, skip animation
      if (queuedCount === 0 && newCount >= 10) {
        visibleLetters.value = [...letters];
        queuedCount = newCount;
        queueDrained.value = true;
        options.onQueueDrained?.();
        return;
      }

      const wasIdle = queuedCount === visibleLetters.value.length;
      queuedCount = newCount;

      // If queue was idle, start processing immediately
      if (wasIdle) {
        processNext();
      }
      // Otherwise, processNext is already scheduled and will pick up the new letters
    },
    { immediate: true }
  );

  onScopeDispose(() => {
    disposed = true;
    if (timerId !== undefined) {
      clearTimeout(timerId);
      timerId = undefined;
    }
  });

  return {
    visibleLetters,
    isRevealing,
    queueDrained,
  };
}
